import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Plug, Cloud, RefreshCw, BarChart3, Lock, Shield, Target, BookOpen, Wrench } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'
import { MotionTokens } from '../styles/motion-tokens'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const sectionReveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: {
    duration: MotionTokens.sectionReveal.duration,
    ease: [0.22, 1, 0.32, 1],
  },
}

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

const ARCHITECTURE = [
  { icon: Plug, title: 'API-First Integration', desc: 'RESTful and GraphQL APIs with comprehensive SDKs for seamless integration', items: ['OAuth 2.0 / OpenID Connect', 'SAML 2.0 enterprise SSO', 'Webhook-based event streaming'] },
  { icon: Cloud, title: 'Cloud & On-Premise', desc: 'Flexible deployment options to meet your security and compliance requirements', items: ['AWS, Azure, GCP support', 'Private cloud deployment', 'On-premise installation'] },
  { icon: RefreshCw, title: 'Data Synchronization', desc: 'Real-time bidirectional sync with existing enterprise systems', items: ['CRM/ERP integration', 'Data warehouse connectors', 'ETL pipeline support'] },
  { icon: BarChart3, title: 'Monitoring & Analytics', desc: 'Comprehensive observability with custom dashboards and alerts', items: ['Real-time performance metrics', 'Custom alerting rules', 'Usage analytics and reporting'] },
  { icon: Lock, title: 'Security Framework', desc: 'Multi-layer security with encryption, access control, and audit logging', items: ['End-to-end encryption', 'Role-based access control', 'Comprehensive audit trails'] },
  { icon: Shield, title: 'Compliance Ready', desc: 'Pre-configured for major regulatory frameworks and industry standards', items: ['GDPR, CCPA, HIPAA', 'SOC 2, ISO 27001 target', 'Industry-specific standards'] },
]

const STEPS = [
  { step: '1', title: 'Discovery', desc: 'Assessment of current systems, requirements gathering, and architecture design', time: '2-4 weeks' },
  { step: '2', title: 'Pilot Deployment', desc: 'Limited rollout to test integration, train users, and validate performance', time: '4-8 weeks' },
  { step: '3', title: 'Scaled Rollout', desc: 'Phased expansion across departments with continuous monitoring and optimization', time: '8-16 weeks' },
  { step: '4', title: 'Optimization', desc: 'Performance tuning, advanced features, and continuous improvement', time: 'Ongoing' },
]

const SUPPORT = [
  { icon: Target, title: 'Dedicated Support Team', desc: 'Named customer success manager and technical account manager', sub: '24/7 priority support with <1hr response SLA' },
  { icon: BookOpen, title: 'Training & Onboarding', desc: 'Comprehensive training programs for administrators and end users', sub: 'On-site workshops, webinars, and certification programs' },
  { icon: Wrench, title: 'Custom Development', desc: 'Tailored features and integrations to meet unique requirements', sub: 'Professional services for bespoke solutions' },
]

export default function EnterprisePage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1
              className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
              style={brandFont}
            >
              Enterprise <span className="text-transparent bg-clip-text bg-trust-gradient">Integration</span>
            </h1>
            <p className="text-xl text-white/70 max-w-4xl mx-auto" style={bodyFont}>
              Seamless deployment, enterprise-grade security, and white-glove support for
              large-scale consciousness technology implementations
            </p>
          </motion.div>
        </div>
      </section>

      {/* Architecture Overview */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-4xl tracking-[0.1em] mb-4 text-white"
              style={brandFont}
            >
              Enterprise <span className="text-trust-blue">Architecture</span>
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              LUKHAS consciousness technology integrates seamlessly with your existing
              enterprise infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ARCHITECTURE.map(({ icon: Icon, title, desc, items }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8">
                    <div className="mb-4">
                      <Icon className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl tracking-wide mb-3 text-trust-blue" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/70 mb-4" style={bodyFont}>{desc}</p>
                    <ul className="space-y-2 text-sm text-white/50">
                      {items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="text-trust-blue mt-0.5">&#8226;</span>
                          <span style={bodyFont}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-4xl tracking-[0.1em] mb-4 text-white"
              style={brandFont}
            >
              Implementation <span className="text-trust-blue">Process</span>
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Phased deployment strategy designed to minimize disruption and maximize ROI
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map(({ step, title, desc, time }) => (
              <motion.div key={step} {...sectionReveal}>
                <GlassCard>
                  <div className="p-6 text-center">
                    <div
                      className="text-4xl mb-4 text-trust-blue"
                      style={{ ...brandFont, fontWeight: 200 }}
                    >
                      {step}
                    </div>
                    <h3 className="text-lg tracking-wide mb-2 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-sm text-white/50" style={bodyFont}>{desc}</p>
                    <p className="text-xs text-white/30 mt-3" style={bodyFont}>{time}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Services */}
      <section id="support" className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-4xl tracking-[0.1em] mb-4 text-white"
              style={brandFont}
            >
              Enterprise <span className="text-trust-blue">Support</span>
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              White-glove service with dedicated teams ensuring your success
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {SUPPORT.map(({ icon: Icon, title, desc, sub }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8 text-center">
                    <div className="mb-4 flex justify-center">
                      <Icon className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl tracking-wide mb-3 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/70 mb-4" style={bodyFont}>{desc}</p>
                    <p className="text-sm text-white/50" style={bodyFont}>{sub}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <GlassCard className="max-w-4xl mx-auto">
          <div className="p-12 text-center">
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Start Your Enterprise Transformation
            </h2>
            <p className="text-xl text-white/70 mb-12" style={bodyFont}>
              Let&apos;s discuss your integration requirements and deployment timeline
            </p>
            <Link
              to="/contact"
              className="px-12 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
              style={bodyFont}
            >
              Request Enterprise Demo
            </Link>
          </div>
        </GlassCard>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Solutions',
            links: [
              { label: 'Financial Services', href: '/solutions' },
              { label: 'Healthcare', href: '/solutions' },
              { label: 'Manufacturing', href: '/solutions' },
            ],
          },
          {
            title: 'Enterprise',
            links: [
              { label: 'Integration', href: '/enterprise' },
              { label: 'Security', href: '/enterprise' },
              { label: 'Support', href: '/enterprise' },
            ],
          },
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
        ]}
      />
    </div>
  )
}
